<template>
  <b-sidebar
    id="quote-log-panel"
    :visible="panel.open"
    right
    backdrop
    shadow
    no-header
    lazy
    bg-variant="white"
    sidebar-class="quote-log"
    aria-labelledby="quote-log-title"
    @change="onVisibilityChange"
    @shown="focusComposer"
    @hidden="restoreFocus"
  >
    <template #default="{ hide }">
      <header class="quote-log__header">
        <div class="quote-log__heading">
          <h4
            id="quote-log-title"
            class="quote-log__title"
          >
            Bitácora<template v-if="folio">
              · #{{ folio }}
            </template>
          </h4>
          <p class="quote-log__subtitle">
            <feather-icon
              icon="LockIcon"
              size="12"
              aria-hidden="true"
            />
            Solo la ve el equipo de YoConstruyo
          </p>
        </div>
        <b-button
          variant="flat-secondary"
          class="btn-icon quote-log__close"
          aria-label="Cerrar bitácora"
          @click="hide"
        >
          <feather-icon
            icon="XIcon"
            size="20"
          />
        </b-button>
      </header>

      <b-form
        class="quote-log__composer"
        novalidate
        @submit.prevent="addNote"
      >
        <label
          for="quote-log-new"
          class="quote-log__label"
        >Nueva nota</label>
        <b-form-textarea
          id="quote-log-new"
          ref="composer"
          :value="draft"
          rows="3"
          max-rows="10"
          :maxlength="maxLength"
          :disabled="saving"
          :state="composerError ? false : null"
          placeholder="Qué pasó, con quién y qué sigue"
          aria-describedby="quote-log-new-help quote-log-new-error"
          @input="setDraft"
          @keydown.enter="submitOnShortcut($event, addNote)"
        />
        <b-form-invalid-feedback
          id="quote-log-new-error"
          class="quote-log__error"
          role="alert"
          :state="composerError ? false : null"
        >
          {{ composerError }}
        </b-form-invalid-feedback>
        <div class="quote-log__composer-footer">
          <small
            id="quote-log-new-help"
            class="quote-log__help"
          >
            <span class="quote-log__shortcut">Ctrl + Enter para guardar</span>
            <span v-if="draft.length > maxLength * 0.8">{{ draft.length }}/{{ maxLength }}</span>
          </small>
          <b-button
            type="submit"
            variant="primary"
            size="sm"
            :disabled="saving"
          >
            <b-spinner
              v-if="saving"
              small
              class="mr-50"
            />
            Guardar nota
          </b-button>
        </div>
      </b-form>

      <div class="quote-log__filters">
        <b-form-radio-group
          v-model="filter"
          buttons
          button-variant="outline-primary"
          size="sm"
          aria-label="Qué mostrar en la bitácora"
          :options="filterOptions"
        />
      </div>

      <section
        class="quote-log__body"
        aria-labelledby="quote-log-title"
        :aria-busy="loading ? 'true' : 'false'"
      >
        <div
          v-if="loading && !entries.length"
          aria-hidden="true"
        >
          <div
            v-for="row in 3"
            :key="row"
            class="quote-log__skeleton"
          >
            <b-skeleton
              type="avatar"
              size="2rem"
            />
            <div class="quote-log__skeleton-lines">
              <b-skeleton width="45%" />
              <b-skeleton width="85%" />
            </div>
          </div>
        </div>

        <div
          v-else-if="loadError"
          class="quote-log__state"
          role="alert"
        >
          <p class="quote-log__state-title">
            No pudimos cargar la bitácora
          </p>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="load"
          >
            Intentar de nuevo
          </b-button>
        </div>

        <div
          v-else-if="!visibleEntries.length"
          class="quote-log__state"
        >
          <span
            class="quote-log__state-icon"
            aria-hidden="true"
          >
            <feather-icon
              icon="MessageSquareIcon"
              size="20"
            />
          </span>
          <p class="quote-log__state-title">
            {{ filter === 'notes' ? 'Todavía no hay notas' : 'Todavía no hay actividad' }}
          </p>
          <p class="quote-log__state-text">
            Escribe arriba lo que vaya pasando con esta cotización: acuerdos con el cliente, avisos del proveedor, pagos.
          </p>
        </div>

        <div v-else>
          <div
            v-for="group in groups"
            :key="group.key"
            class="quote-log__day"
          >
            <h5 class="quote-log__day-label">
              {{ group.label }}
            </h5>
            <ol class="quote-log__list">
              <li
                v-for="entry in group.entries"
                :key="`${entry.kind}-${entry.id}`"
                :class="entry.kind === 'note' ? 'quote-log__note' : 'quote-log__event'"
              >
                <template v-if="entry.kind === 'note'">
                  <span
                    class="quote-log__avatar"
                    aria-hidden="true"
                  >{{ initials(entry.author) }}</span>
                  <div class="quote-log__note-main">
                    <div class="quote-log__meta">
                      <span class="quote-log__author">{{ authorName(entry.author) }}</span>
                      <time
                        :datetime="entry.created_at"
                        :title="fullDate(entry.created_at)"
                      >{{ time(entry.created_at) }}</time>
                      <span
                        v-if="entry.edited_at"
                        class="quote-log__edited"
                        :title="`Corregida el ${fullDate(entry.edited_at)}`"
                      >· editada</span>
                    </div>

                    <b-form
                      v-if="editingId === entry.id"
                      class="quote-log__edit"
                      novalidate
                      @submit.prevent="saveEdit(entry)"
                    >
                      <label
                        :for="`quote-log-edit-${entry.id}`"
                        class="sr-only"
                      >Corregir nota</label>
                      <b-form-textarea
                        :id="`quote-log-edit-${entry.id}`"
                        ref="editor"
                        v-model="editText"
                        rows="3"
                        max-rows="10"
                        :maxlength="maxLength"
                        :disabled="editSaving"
                        :state="editError ? false : null"
                        :aria-describedby="`quote-log-edit-error-${entry.id}`"
                        @keydown.enter="submitOnShortcut($event, () => saveEdit(entry))"
                        @keydown.esc.stop="cancelEdit"
                      />
                      <b-form-invalid-feedback
                        :id="`quote-log-edit-error-${entry.id}`"
                        class="quote-log__error"
                        role="alert"
                        :state="editError ? false : null"
                      >
                        {{ editError }}
                      </b-form-invalid-feedback>
                      <div class="quote-log__edit-actions">
                        <b-button
                          type="submit"
                          variant="primary"
                          size="sm"
                          :disabled="editSaving"
                        >
                          <b-spinner
                            v-if="editSaving"
                            small
                            class="mr-50"
                          />
                          Guardar cambios
                        </b-button>
                        <b-button
                          variant="flat-secondary"
                          size="sm"
                          :disabled="editSaving"
                          @click="cancelEdit"
                        >
                          Cancelar
                        </b-button>
                      </div>
                    </b-form>
                    <p
                      v-else
                      class="quote-log__text"
                      v-text="entry.body"
                    />
                  </div>
                  <b-dropdown
                    v-if="entry.mine && editingId !== entry.id"
                    variant="link"
                    no-caret
                    right
                    size="sm"
                    toggle-class="quote-log__menu"
                  >
                    <template #button-content>
                      <feather-icon
                        icon="MoreVerticalIcon"
                        size="16"
                        aria-hidden="true"
                      />
                      <span class="sr-only">Opciones de la nota</span>
                    </template>
                    <b-dropdown-item @click="startEdit(entry)">
                      <feather-icon
                        icon="Edit2Icon"
                        size="14"
                        class="mr-50"
                      />
                      Corregir
                    </b-dropdown-item>
                    <b-dropdown-item
                      variant="danger"
                      @click="removeNote(entry)"
                    >
                      <feather-icon
                        icon="Trash2Icon"
                        size="14"
                        class="mr-50"
                      />
                      Borrar
                    </b-dropdown-item>
                  </b-dropdown>
                </template>

                <template v-else>
                  <span
                    class="quote-log__event-icon"
                    :class="`is-${eventTone(entry.action_type)}`"
                    aria-hidden="true"
                  >
                    <feather-icon
                      :icon="eventIcon(entry.action_type)"
                      size="13"
                    />
                  </span>
                  <div class="quote-log__event-main">
                    <p class="quote-log__event-title">
                      {{ entry.title }}
                      <span
                        v-if="entry.count > 1"
                        class="quote-log__event-count"
                      >· {{ entry.count }} veces</span>
                    </p>
                    <p
                      v-if="entry.detail"
                      class="quote-log__event-detail"
                    >
                      {{ entry.detail }}
                    </p>
                    <p class="quote-log__event-meta">
                      <template v-if="entry.author">
                        {{ authorName(entry.author) }}<template v-if="entry.author.role_name !== 'admin'">
                          ({{ entry.author.role_name | roleName }})
                        </template> ·
                      </template>
                      <time
                        :datetime="entry.created_at"
                        :title="fullDate(entry.created_at)"
                      >{{ eventTime(entry) }}</time>
                    </p>
                  </div>
                </template>
              </li>
            </ol>
          </div>
        </div>
      </section>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BButton, BForm, BFormTextarea, BFormInvalidFeedback, BFormRadioGroup, BSkeleton, BSpinner,
  BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const MAX_LENGTH = 5000

const EVENT_ICONS = {
  create: 'FilePlusIcon',
  publish: 'SendIcon',
  send_cart_link: 'LinkIcon',
  bidding: 'TrendingDownIcon',
  bid: 'TagIcon',
  close_quote_bidding: 'FlagIcon',
  buyout: 'ShoppingCartIcon',
  pay_quote: 'UploadIcon',
  approve_customer_payment: 'CheckCircleIcon',
  reject_customer_payment: 'XCircleIcon',
  credit_payment: 'CreditCardIcon',
  update_delivery_status: 'TruckIcon',
  register_item_delivery: 'TruckIcon',
  remove_item_delivery: 'TruckIcon',
  remove_freight: 'TruckIcon',
  update_freight: 'TruckIcon',
  clear_freight_override: 'TruckIcon',
  update_quote_item_order_number: 'ListIcon',
  update_quote_item_prices: 'EditIcon',
  change_quote_item_product: 'RepeatIcon',
  merge_quote_item: 'GitMergeIcon',
  delete_quote_items: 'Trash2Icon',
  requote: 'CopyIcon',
  soft_delete: 'Trash2Icon',
  restore: 'RotateCcwIcon',
  whatsapp_capture_confirmed: 'MessageCircleIcon',
}

const EVENT_TONES = {
  approve_customer_payment: 'success',
  credit_payment: 'success',
  reject_customer_payment: 'danger',
  delete_quote_items: 'danger',
  soft_delete: 'danger',
}

const dayKey = date => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`

export default {
  components: {
    BSidebar,
    BButton,
    BForm,
    BFormTextarea,
    BFormInvalidFeedback,
    BFormRadioGroup,
    BSkeleton,
    BSpinner,
    BDropdown,
    BDropdownItem,
  },

  data() {
    return {
      maxLength: MAX_LENGTH,
      entries: [],
      loading: false,
      loadError: false,
      saving: false,
      composerError: '',
      filter: 'all',
      editingId: null,
      editText: '',
      editError: '',
      editSaving: false,
      requestId: 0,
      returnFocusTo: null,
    }
  },

  computed: {
    ...mapGetters('quoteNotes', ['panel', 'draftFor']),

    quoteId() {
      return this.panel.quoteId
    },

    activeQuoteId() {
      return this.panel.open ? this.panel.quoteId : null
    },

    folio() {
      return this.panel.folio
    },

    draft() {
      return this.draftFor(this.quoteId)
    },

    notesCount() {
      return this.entries.filter(entry => entry.kind === 'note').length
    },

    filterOptions() {
      return [
        { text: `Todo · ${this.entries.length}`, value: 'all' },
        { text: `Solo notas · ${this.notesCount}`, value: 'notes' },
      ]
    },

    visibleEntries() {
      return this.filter === 'notes' ? this.entries.filter(entry => entry.kind === 'note') : this.entries
    },

    groups() {
      const groups = []
      this.visibleEntries.forEach(entry => {
        const date = new Date(entry.created_at)
        const key = dayKey(date)
        const last = groups[groups.length - 1]
        if (last && last.key === key) last.entries.push(entry)
        else groups.push({ key, label: this.dayLabel(date), entries: [entry] })
      })
      return groups
    },
  },

  watch: {
    activeQuoteId: {
      immediate: true,
      handler(quoteId) {
        if (!quoteId) return
        if (!this.$el?.contains?.(document.activeElement)) this.returnFocusTo = document.activeElement
        this.reset()
      },
    },
  },

  methods: {
    ...mapActions('quoteNotes', ['fetchQuoteTimeline', 'createQuoteNote', 'updateQuoteNote', 'deleteQuoteNote', 'syncSummaryFromTimeline']),
    ...mapMutations('quoteNotes', { closePanel: 'CLOSE_PANEL', storeDraft: 'SET_DRAFT', setFolio: 'SET_PANEL_FOLIO' }),

    reset() {
      this.entries = []
      this.filter = 'all'
      this.composerError = ''
      this.cancelEdit()
      this.load()
    },

    async load() {
      if (!this.quoteId) return
      const requestId = this.requestId + 1
      this.requestId = requestId
      this.loading = true
      this.loadError = false
      try {
        const response = await this.fetchQuoteTimeline(this.quoteId)
        if (requestId !== this.requestId) return
        this.entries = response.data || []
        if (response.meta?.folio) this.setFolio(response.meta.folio)
      } catch (error) {
        if (requestId === this.requestId) this.loadError = true
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },

    onVisibilityChange(visible) {
      if (!visible && this.panel.open) this.closePanel()
    },

    restoreFocus() {
      const target = this.returnFocusTo
      this.returnFocusTo = null
      if (target && document.body.contains(target)) target.focus()
    },

    focusComposer() {
      this.$nextTick(() => this.$refs.composer?.focus())
    },

    setDraft(text) {
      this.composerError = ''
      this.storeDraft({ quoteId: this.quoteId, text })
    },

    submitOnShortcut(event, submit) {
      if (!(event.ctrlKey || event.metaKey)) return
      event.preventDefault()
      submit()
    },

    validationError(text) {
      if (!text.trim()) return 'Escribe la nota antes de guardarla.'
      if (text.length > MAX_LENGTH) return `La nota puede tener hasta ${MAX_LENGTH} caracteres.`
      return ''
    },

    syncSummary() {
      this.syncSummaryFromTimeline({ quoteId: this.quoteId, entries: this.entries })
    },

    async addNote() {
      const { quoteId } = this
      this.composerError = this.validationError(this.draft)
      if (this.composerError) {
        this.focusComposer()
        return
      }

      this.saving = true
      try {
        const response = await this.createQuoteNote({ quoteId, body: this.draft })
        this.storeDraft({ quoteId, text: '' })
        if (quoteId !== this.quoteId) return
        this.entries = [response.data, ...this.entries]
        this.syncSummary()
        this.focusComposer()
      } catch (error) {
        this.composerError = error?.response?.data?.messages?.[0] || 'No pudimos guardar la nota. Intenta de nuevo.'
      } finally {
        this.saving = false
      }
    },

    startEdit(entry) {
      this.editingId = entry.id
      this.editText = entry.body
      this.editError = ''
      this.$nextTick(() => {
        const [editor] = [].concat(this.$refs.editor || [])
        editor?.focus()
      })
    },

    cancelEdit() {
      this.editingId = null
      this.editText = ''
      this.editError = ''
    },

    async saveEdit(entry) {
      this.editError = this.validationError(this.editText)
      if (this.editError) return

      this.editSaving = true
      try {
        const response = await this.updateQuoteNote({ quoteId: this.quoteId, noteId: entry.id, body: this.editText })
        this.entries = this.entries.map(item => (item.kind === 'note' && item.id === entry.id ? response.data : item))
        this.syncSummary()
        this.cancelEdit()
      } catch (error) {
        this.editError = error?.response?.data?.messages?.[0] || 'No pudimos guardar los cambios. Intenta de nuevo.'
      } finally {
        this.editSaving = false
      }
    },

    async removeNote(entry) {
      const confirmed = await this.$bvModal.msgBoxConfirm('Ya no se verá en la bitácora. No se puede deshacer.', {
        title: '¿Borrar esta nota?',
        okTitle: 'Borrar nota',
        okVariant: 'danger',
        cancelTitle: 'Cancelar',
        cancelVariant: 'outline-secondary',
        centered: true,
        autoFocusButton: 'cancel',
      })
      if (!confirmed) return

      try {
        await this.deleteQuoteNote({ quoteId: this.quoteId, noteId: entry.id })
        this.entries = this.entries.filter(item => !(item.kind === 'note' && item.id === entry.id))
        this.syncSummary()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.messages?.[0] || 'No pudimos borrar la nota. Intenta de nuevo.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    authorName(author) {
      return author?.name || 'Usuario eliminado'
    },

    initials(author) {
      const parts = String(author?.name || '?').trim().split(/\s+/)
      return parts.slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('')
    },

    time(value) {
      return new Date(value).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
    },

    eventTime(entry) {
      if (entry.count > 1 && entry.started_at) return `${this.time(entry.started_at)} – ${this.time(entry.created_at)}`
      return this.time(entry.created_at)
    },

    fullDate(value) {
      return new Date(value).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' })
    },

    dayLabel(date) {
      const today = new Date()
      const yesterday = new Date()
      yesterday.setDate(today.getDate() - 1)
      if (dayKey(date) === dayKey(today)) return 'Hoy'
      if (dayKey(date) === dayKey(yesterday)) return 'Ayer'
      const options = { weekday: 'long', day: 'numeric', month: 'short' }
      if (date.getFullYear() !== today.getFullYear()) options.year = 'numeric'
      return date.toLocaleDateString('es-MX', options)
    },

    eventIcon(actionType) {
      return EVENT_ICONS[actionType] || 'ActivityIcon'
    },

    eventTone(actionType) {
      return EVENT_TONES[actionType] || 'neutral'
    },
  },
}
</script>

<style lang="scss" scoped>
$brand: #340668;
$border: #ebe9f1;
$ink: #25233a;
$muted: #5e5873;
$subtle: #6e6b7b;
$danger-text: #c2393a;
$success-text: #1f7a4a;

::v-deep .quote-log {
  width: 440px;
  max-width: 100%;
}

::v-deep .b-sidebar-body {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.quote-log__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1.25rem 1.25rem 0.75rem;
  border-bottom: 1px solid $border;
}

.quote-log__title {
  margin: 0 0 0.2rem;
  font-weight: 700;
  color: $ink;
}

.quote-log__subtitle {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin: 0;
  font-size: 0.8rem;
  color: $subtle;
}

.quote-log__close {
  flex-shrink: 0;
  min-width: 2.5rem;
  min-height: 2.5rem;
}

.quote-log__composer {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid $border;
  background: #fbfbfd;
}

.quote-log__label {
  margin-bottom: 0.35rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: $ink;
}

.quote-log__composer-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.6rem;
}

.quote-log__help {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  color: $subtle;
}

@media (hover: none) {
  .quote-log__shortcut {
    display: none;
  }
}

.quote-log__error {
  color: $danger-text;
}

.quote-log__filters {
  padding: 0.75rem 1.25rem 0;
}

.quote-log__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.5rem 1.25rem 1.5rem;
}

.quote-log__skeleton {
  display: flex;
  gap: 0.75rem;
  padding: 0.85rem 0;
}

.quote-log__skeleton-lines {
  flex: 1;
}

.quote-log__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2.5rem 1rem;
  text-align: center;
}

.quote-log__state-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  background: rgba($brand, 0.1);
  color: $brand;
}

.quote-log__state-title {
  margin: 0;
  font-weight: 600;
  color: $ink;
}

.quote-log__state-text {
  max-width: 18rem;
  margin: 0;
  font-size: 0.85rem;
  color: $muted;
}

.quote-log__day + .quote-log__day {
  margin-top: 0.5rem;
}

.quote-log__day-label {
  position: sticky;
  top: 0;
  z-index: 1;
  margin: 0;
  padding: 0.75rem 0 0.4rem;
  background: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $subtle;
}

.quote-log__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.quote-log__note {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  margin: 0.4rem 0;
  padding: 0.75rem;
  border: 1px solid $border;
  border-radius: 0.6rem;
  background: #fff;
}

.quote-log__avatar {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: rgba($brand, 0.1);
  color: $brand;
  font-size: 0.75rem;
  font-weight: 700;
}

.quote-log__note-main {
  flex: 1;
  min-width: 0;
}

.quote-log__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem;
  margin-bottom: 0.2rem;
  font-size: 0.78rem;
  color: $subtle;
}

.quote-log__author {
  font-size: 0.85rem;
  font-weight: 600;
  color: $ink;
}

.quote-log__edited {
  font-style: italic;
}

.quote-log__text {
  margin: 0;
  font-size: 0.9rem;
  color: $ink;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.quote-log__edit-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

::v-deep .quote-log__menu {
  min-width: 2rem;
  min-height: 2rem;
  padding: 0.25rem;
  color: $subtle;

  &:focus-visible {
    outline: 2px solid $brand;
    outline-offset: 2px;
  }
}

.quote-log__event {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.5rem 0.75rem;
}

.quote-log__event-icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 1.5rem;
  color: $subtle;

  &.is-success {
    color: $success-text;
  }

  &.is-danger {
    color: $danger-text;
  }
}

.quote-log__event-main {
  min-width: 0;
}

.quote-log__event-title {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: $muted;
}

.quote-log__event-count {
  font-weight: 500;
  color: $subtle;
}

.quote-log__event-detail {
  margin: 0;
  font-size: 0.8rem;
  color: $muted;
  overflow-wrap: anywhere;
}

.quote-log__event-meta {
  margin: 0.1rem 0 0;
  font-size: 0.75rem;
  color: $subtle;
}

@media (max-width: 575.98px) {
  .quote-log__composer-footer .btn,
  .quote-log__edit-actions .btn {
    min-height: 44px;
  }
}
</style>
